import React from 'react';
import styled from 'styled-components';
import { Box, ListItemText, SxProps, Typography, Theme } from '@mui/material';
import { useVirtualizer } from '@tanstack/react-virtual';
import Checkbox from '../Checkbox/Checkbox';
import { Flex, FlexColumn } from '../Flex';
import OverflowTooltip from '../OverflowTooltip';
import { componentToken } from '../../style/utils';
import {
  OPTION_ITEM_HEIGHT,
  OPTION_ITEM_HEIGHT_WITH_DESCRIPTION,
} from './constants';
import MenuItemStyled from './shared/MenuItemStyled';
import { DropDownOption } from './types';

const Divider = styled(Box)`
  background: ${componentToken('dropDown.divider.background')};
  height: 1px;
  width: 100%;
  margin-bottom: ${componentToken('dropDown.divider.marginBottom')};
`;

const Counter = styled(Typography)`
  font-size: ${componentToken('dropDown.counter.fontSize')} !important;
  line-height: normal;
  color: ${componentToken('dropDown.counter.color')};
`;

type Props = {
  optionsContainerRef: any;
  filteredOptions: DropDownOption[];
  handleItemClick: (value: string) => void;
  setInitialSort?: React.Dispatch<React.SetStateAction<any>>;
  selectedItems: string | string[];
  indeterminate?: boolean;
  multiple?: boolean;
  showSelectAll?: boolean;
  showCounter?: boolean;
  optionRenderer?: (val: string) => string;
  emptyValueLabel?: string;
  sx?: SxProps<Theme>;
};

const Options = ({
  optionsContainerRef,
  filteredOptions,
  multiple,
  showSelectAll,
  handleItemClick,
  setInitialSort,
  selectedItems,
  indeterminate,
  optionRenderer,
  showCounter,
  emptyValueLabel = 'N/A',
  sx,
}: Props) => {
  const optionsVirtualizer = useVirtualizer({
    getScrollElement: () => optionsContainerRef.current,
    count: filteredOptions?.length,
    overscan: 20,
    estimateSize: (index) =>
      filteredOptions[index]?.description
        ? OPTION_ITEM_HEIGHT_WITH_DESCRIPTION
        : OPTION_ITEM_HEIGHT,
  });

  const virtualOptions = optionsVirtualizer.getVirtualItems();

  const totalOptionsSize = optionsVirtualizer.getTotalSize();

  const paddingTop =
    virtualOptions.length > 0 ? virtualOptions?.[0]?.start || 4 : 4;

  const paddingBottom =
    virtualOptions.length > 0
      ? totalOptionsSize -
          (virtualOptions?.[virtualOptions.length - 1]?.end || 4) || 4
      : 4;

  return (
    <Box
      sx={{
        pt: `${paddingTop}px`,
        pb: `${paddingBottom}px`,
        px: '4px',
        width: '100%',

        ...sx,
      }}
    >
      {showSelectAll && (
        <MenuItemStyled
          value="all"
          $multiple={multiple}
          onClick={() => handleItemClick('all')}
          selected={selectedItems?.includes('all')}
          disabled={filteredOptions.length === 0}
          sx={{ mt: 0.5, mb: 1, display: 'flex', gap: 1 }}
        >
          <Checkbox
            checked={selectedItems?.includes('all')}
            indeterminate={indeterminate}
          />
          <ListItemText sx={{ paddingRight: 1 }} primary="Select all" />
        </MenuItemStyled>
      )}
      {showSelectAll && <Divider />}
      {virtualOptions.map((virtualRow: any) => (
        <MenuItemStyled
          key={virtualRow.key}
          value={filteredOptions[virtualRow.index]?.value}
          onClick={(e) => {
            e.stopPropagation();
            handleItemClick(filteredOptions[virtualRow.index]?.value);
            setInitialSort?.(false);
          }}
          $multiple={multiple}
          selected={
            multiple
              ? selectedItems?.includes(
                  filteredOptions[virtualRow.index]?.value,
                )
              : selectedItems === filteredOptions[virtualRow.index]?.value
          }
          $hasDescription={!!filteredOptions[virtualRow.index]?.description}
          disabled={filteredOptions[virtualRow.index]?.disabled}
        >
          {multiple && (
            <Checkbox
              checked={
                (selectedItems as string[]).filter(
                  (selected) =>
                    selected === filteredOptions[virtualRow.index]?.value,
                ).length > 0
              }
            />
          )}
          <Flex
            sx={{
              alignItems: 'center',
              gap: 1,
              overflowX: 'hidden',
              width: '100%',
              paddingRight: 1,
            }}
          >
            <Flex>{filteredOptions[virtualRow.index]?.icon}</Flex>

            <Flex
              alignItems="center"
              justifyContent="space-between"
              width="98%"
            >
              {optionRenderer ? (
                <OverflowTooltip
                  typographySx={{ maxWidth: '98%' }}
                  aria-label="dropdown-option"
                >
                  {optionRenderer(filteredOptions[virtualRow.index]?.value)}
                </OverflowTooltip>
              ) : (
                <FlexColumn width="98%" gap={0.2}>
                  <OverflowTooltip
                    typographySx={{ maxWidth: '98%' }}
                    aria-label="dropdown-option"
                  >
                    {filteredOptions[virtualRow.index]?.label ||
                      emptyValueLabel}
                  </OverflowTooltip>

                  {filteredOptions[virtualRow.index]?.description && (
                    <OverflowTooltip
                      typographySx={{
                        maxWidth: '98%',
                        fontSize: componentToken('dropDown.options.fontSize'),
                        lineHeight: 'normal',
                        color: componentToken('dropDown.options.color'),
                      }}
                    >
                      {filteredOptions[virtualRow.index]?.description}
                    </OverflowTooltip>
                  )}
                </FlexColumn>
              )}

              {showCounter && (
                <Counter>
                  {filteredOptions[virtualRow.index]?.counter || 0}
                </Counter>
              )}
            </Flex>
          </Flex>
        </MenuItemStyled>
      ))}
    </Box>
  );
};

export default Options;
